import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { ActionIcon, Button, Divider } from '@mantine/core';
import {
  ArrowLeft,
  ArrowsMaximize,
  Edit,
  GripVertical,
  TextSize,
  Trash,
  X
} from 'tabler-icons-react';
import AppStack from '../../common/AppStack';
import AppFlexbox from '../../common/AppFlexbox';
import AppCard from '../../common/AppCard';
import AppText from '../../common/AppText';
import SortableList from '../../common/SortableList';
import AppImage from '../../common/AppImage';
import * as classes from '../../../styles/nestedStyles.module.css';
import PrintDesignEditorCanvasItemPositioningSection from './PrintDesignEditorCanvasItemPositioningSection';
import PrintDesignEditorCanvasItemPropertiesSection from './PrintDesignEditorCanvasItemPropertiesSection';
import {
  ECOM_PRINT_DESIGN_ITEM_TYPE_ENUM,
  ECOM_PRINT_DESIGN_ITEM_TYPE_LIST
} from '../../../config/constants';

const PrintDesignEditorCanvasLayersControls = ({
  loading,
  canvasController,
  canvasItems,
  selectedObjectIndex,
  selectedObject,
  viewState,
  setViewState
}) => {
  const [showItemPropertiesEdit, setShowItemPropertiesEdit] = useState(false);
  const {
    selectObjectById,
    reorderCanvasObjectsBySort,
    deleteObjectById
  } = canvasController;

  const selectedObjectResolution = selectedObject
    ? (selectedObject.width / selectedObject.currentWidthInInches).toFixed(0)
    : 0;
  const resolutionImageStatus =
    selectedObjectResolution < 100
      ? {
          label: 'Low resolution',
          color: '#C40000'
        }
      : selectedObjectResolution < 200
      ? {
          label: 'Medium resolution',
          color: '#eca70a'
        }
      : {
          label: 'High resolution',
          color: '#666'
        };
  const { label: selectedObjectTypeLabel } =
    ECOM_PRINT_DESIGN_ITEM_TYPE_LIST.find(
      (type) => type.value === selectedObject?.fkEcomPrintDesignItemType
    ) || {};

  useEffect(() => {
    setShowItemPropertiesEdit(false);
  }, [selectedObject?.id, viewState.showSelectedLayerControls]);

  return selectedObject && viewState.showSelectedLayerControls ? (
    <AppCard radius={4} shadow="md" style={{ padding: 0 }} withBorder>
      <AppStack style={{ gap: 0 }}>
        <AppFlexbox
          style={{
            gap: 5,
            padding: 8,
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <AppFlexbox
            style={{
              alignItems: 'center',
              gap: 5
            }}
          >
            <ActionIcon
              color="darks"
              onClick={() => {
                setViewState((prev) => ({
                  ...prev,
                  showSelectedLayerControls: false
                }));
              }}
              variant="outline"
            >
              <ArrowLeft size={20} />
            </ActionIcon>
            <AppFlexbox style={{ gap: 8, alignItems: 'center' }}>
              <AppText style={{ fontSize: 18, fontWeight: 500 }}>
                Layer {selectedObjectIndex + 1}
              </AppText>
            </AppFlexbox>
          </AppFlexbox>
          <ActionIcon
            color="darks"
            onClick={() => {
              setViewState((prev) => ({
                ...prev,
                showLayerControls: !prev.showLayerControls
              }));
            }}
            variant="subtle"
          >
            <X size={20} />
          </ActionIcon>
        </AppFlexbox>
        <Divider />
        <AppStack style={{ padding: 8, gap: 8 }}>
          <AppFlexbox
            style={{
              alignItems: 'center',
              gap: 5
            }}
          >
            <AppFlexbox style={{ gap: 8, alignItems: 'center' }}>
              {selectedObject?.fkEcomPrintDesignItemType ===
              ECOM_PRINT_DESIGN_ITEM_TYPE_ENUM.IMAGE ? (
                <>
                  <AppStack style={{ width: 60, height: 60 }}>
                    <AppImage src={selectedObject.src} />
                  </AppStack>
                  <AppStack style={{ gap: 0 }}>
                    <AppText style={{ fontSize: 16, fontWeight: 500 }}>
                      {selectedObjectTypeLabel}
                    </AppText>

                    <AppText
                      style={{
                        fontSize: 12,
                        color: resolutionImageStatus.color
                      }}
                    >
                      {resolutionImageStatus.label} ({selectedObjectResolution}{' '}
                      DPI)
                    </AppText>
                  </AppStack>
                </>
              ) : (
                selectedObject?.fkEcomPrintDesignItemType ===
                  ECOM_PRINT_DESIGN_ITEM_TYPE_ENUM.PERSONALIZED_TEXT && (
                  <>
                    <AppStack
                      style={{
                        width: 60,
                        height: 60,
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: 'solid 1px #dee2e6'
                      }}
                    >
                      <TextSize size={40} />
                    </AppStack>
                    <AppStack style={{ gap: 5 }}>
                      <AppText style={{ fontSize: 16, fontWeight: 500 }}>
                        {selectedObjectTypeLabel}
                      </AppText>

                      <Button
                        color="dark"
                        leftSection={
                          showItemPropertiesEdit ? (
                            <ArrowsMaximize size={16} />
                          ) : (
                            <Edit size={16} />
                          )
                        }
                        onClick={() =>
                          setShowItemPropertiesEdit(!showItemPropertiesEdit)
                        }
                        size="compact-sm"
                        variant="outline"
                      >
                        {showItemPropertiesEdit
                          ? 'Show positioning'
                          : 'Edit properties'}
                      </Button>
                    </AppStack>
                  </>
                )
              )}
            </AppFlexbox>
          </AppFlexbox>

          {showItemPropertiesEdit ? (
            <PrintDesignEditorCanvasItemPropertiesSection
              canvasController={canvasController}
              loading={loading}
              selectedObject={selectedObject}
            />
          ) : (
            <PrintDesignEditorCanvasItemPositioningSection
              canvasController={canvasController}
              loading={loading}
              selectedObject={selectedObject}
            />
          )}
        </AppStack>
      </AppStack>
    </AppCard>
  ) : (
    <AppCard radius={4} shadow="md" style={{ padding: 0 }} withBorder>
      <AppStack style={{ gap: 0 }}>
        <AppFlexbox
          style={{
            padding: 8,
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <AppText style={{ fontSize: 18, fontWeight: 500 }}>
            Manage layers
          </AppText>
          <ActionIcon
            color="darks"
            onClick={() => {
              setViewState((prev) => ({
                ...prev,
                showLayerControls: !prev.showLayerControls
              }));
            }}
            variant="subtle"
          >
            <X size={20} />
          </ActionIcon>
        </AppFlexbox>
        <Divider />
        <AppStack style={{ padding: 8, gap: 8 }}>
          <SortableList
            items={canvasItems
              .sort((a, b) => a.sort - b.sort)
              .map((i) => ({
                ...i,
                key: i.id,
                sort: i.sort || 0
              }))}
            listProps={{ style: { gap: 8 } }}
            onChange={(newList) => {
              reorderCanvasObjectsBySort(
                newList.map((p, index) => ({
                  ...p,
                  sort: index + 1
                }))
              );
            }}
            renderItem={(
              item,
              {
                attributes,
                listeners,
                isDragging,
                setNodeRef,
                style,
                isOverlay
              },
              index
            ) => {
              const selected =
                selectedObject &&
                (selectedObject.id === item.id ||
                  selectedObject.id === item.containerId);

              return (
                <AppCard
                  ref={setNodeRef}
                  className={classes['hover-action-card']}
                  onClick={() => {
                    selectObjectById(item.containerId || item.id);
                    setViewState((prev) => ({
                      ...prev,
                      showSelectedLayerControls: true
                    }));
                  }}
                  radius={4}
                  shadow="none"
                  style={{
                    ...style,
                    padding: 0,
                    outline: isOverlay ? 'solid 1px #dee2e6' : 'none',
                    backgroundColor: isDragging
                      ? 'rgba(56, 56, 56, 0.1)'
                      : '#FFF',
                    opacity: isDragging ? 0.4 : 1,
                    cursor: isDragging ? 'grabbing' : 'pointer',
                    border: `solid 1px ${selected ? 'dodgerblue' : '#dee2e6'}`
                  }}
                >
                  <AppFlexbox
                    style={{
                      padding: 8,
                      gap: 8,
                      flex: 1,
                      alignItems: 'center'
                    }}
                  >
                    <ActionIcon
                      color="dark"
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                      variant="subtle"
                      {...attributes}
                      {...listeners}
                    >
                      <GripVertical size={20} />
                    </ActionIcon>

                    <AppFlexbox
                      style={{
                        flex: 1,
                        gap: 8,
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}
                    >
                      <AppFlexbox style={{ alignItems: 'center' }}>
                        <AppStack
                          style={{
                            width: 40,
                            height: 40,
                            border: 'solid 1px #dee2e6'
                          }}
                        >
                          <AppImage h={40} src={item.src} w={40} />
                        </AppStack>
                        <AppStack style={{ gap: 0 }}>
                          <AppText
                            style={{
                              fontSize: 14,
                              fontWeight: selected ? 500 : 'normal'
                            }}
                          >
                            Layer {isOverlay ? '' : item.sort}
                          </AppText>
                        </AppStack>
                      </AppFlexbox>

                      <ActionIcon
                        color="dark"
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteObjectById(item.id);
                        }}
                        radius="md"
                        variant="subtle"
                      >
                        <Trash size={20} />
                      </ActionIcon>
                    </AppFlexbox>
                  </AppFlexbox>
                </AppCard>
              );
            }}
          />
        </AppStack>
      </AppStack>
    </AppCard>
  );
};

PrintDesignEditorCanvasLayersControls.propTypes = {
  canvasController: PropTypes.object,
  canvasItems: PropTypes.array,
  loading: PropTypes.bool,
  selectedObject: PropTypes.object,
  selectedObjectIndex: PropTypes.number,
  setViewState: PropTypes.func,
  viewState: PropTypes.object
};

export default PrintDesignEditorCanvasLayersControls;
